'use client';

import * as React from 'react';
import { useKeyboardClick } from '@federa/components/hooks/useKeyboardClick';
import { useCountdown } from '@federa/components/hooks/useCountdown';
import type { PropertyCardProps } from '@federa/types/ui-property-card';

/**
 * Click handler for a card-action affordance: both share and save sit INSIDE the clickable card, so the default and
 * the bubble must die before the action runs or the card navigates out from under it.
 */
function useCardActionClick(action: ((id: string) => void) | undefined, id: string) {
	return React.useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			action?.(id);
		},
		[action, id],
	);
}

function usePropertyClickHandlers(
	property: PropertyCardProps['property'],
	callbacks: {
		onClick?: (id: string) => void;
		onShare?: (id: string) => void;
		onSave?: (id: string) => void;
	},
) {
	const { onClick, onShare, onSave } = callbacks;
	const handleCardClick = React.useCallback(() => onClick?.(property.id), [onClick, property.id]);
	const handleShareClick = useCardActionClick(onShare, property.id);
	const handleSaveClick = useCardActionClick(onSave, property.id);
	return { handleCardClick, handleShareClick, handleSaveClick };
}

function useImageCarouselHandlers(imageCount: number) {
	const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
	const handlePreviousImage = React.useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			// The zero-image guard is on BOTH updaters: without it the backward wrap target `imageCount - 1` is `-1`, an
			// index no image occupies, and the state cell then carries an out-of-range value into every later step.
			setCurrentImageIndex((prev) => {
				if (imageCount === 0) return prev;
				return prev === 0 ? imageCount - 1 : prev - 1;
			});
		},
		[imageCount],
	);
	const handleNextImage = React.useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			setCurrentImageIndex((prev) => {
				if (imageCount === 0) return prev;
				return prev === imageCount - 1 ? 0 : prev + 1;
			});
		},
		[imageCount],
	);
	return { currentImageIndex, handlePreviousImage, handleNextImage };
}

function useHoverHandlers(hoverable: boolean) {
	const [isHovered, setIsHovered] = React.useState(false);
	const handleMouseEnter = React.useCallback(() => {
		if (hoverable) setIsHovered(true);
	}, [hoverable]);
	const handleMouseLeave = React.useCallback(() => {
		if (hoverable) setIsHovered(false);
	}, [hoverable]);
	return { isHovered, handleMouseEnter, handleMouseLeave };
}

export function usePropertyCardState({
	property,
	onClick,
	onShare,
	onSave,
	hoverable,
}: {
	property: PropertyCardProps['property'];
	onClick?: (id: string) => void;
	onShare?: (id: string) => void;
	onSave?: (id: string) => void;
	hoverable: boolean;
}) {
	const [shareHovered, setShareHovered] = React.useState(false);
	const [saveHovered, setSaveHovered] = React.useState(false);
	const remainingSeconds = useCountdown(property.auctionEndUtc);
	const clickHandlers = usePropertyClickHandlers(property, {
		onClick,
		onShare,
		onSave,
	});
	const carousel = useImageCarouselHandlers(property.images.length);
	const hover = useHoverHandlers(hoverable);
	const { handleKeyDown } = useKeyboardClick({
		onClick: clickHandlers.handleCardClick,
		disabled: !onClick,
	});
	return {
		isHovered: hover.isHovered,
		shareHovered,
		setShareHovered,
		saveHovered,
		setSaveHovered,
		currentImageIndex: carousel.currentImageIndex,
		remainingSeconds,
		handleCardClick: clickHandlers.handleCardClick,
		handleShareClick: clickHandlers.handleShareClick,
		handleSaveClick: clickHandlers.handleSaveClick,
		handlePreviousImage: carousel.handlePreviousImage,
		handleNextImage: carousel.handleNextImage,
		handleMouseEnter: hover.handleMouseEnter,
		handleMouseLeave: hover.handleMouseLeave,
		handleKeyDown,
	};
}
